import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   Order.js — User order history, tracking, and new order placement
   Sidebar matches Profile.js / UserDashboard.js design system
   Tabs: My Orders · Track Order · Place Order
───────────────────────────────────────────────────────────────────────────── */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
:root{
  --navy:#06112b;--navy2:#0d1f45;--navy3:#112359;
  --blue:#1c73ba;--blue2:#2b8de0;--blue3:#5aaef7;
  --gold:#f5a623;--gold2:#ffc94d;
  --green:#34d399;--red:#f87171;--amber:#fbbf24;--purple:#818cf8;
  --text:#e8f4fd;--muted:#94a3b8;--dim:rgba(255,255,255,0.06);
  --border:rgba(28,115,186,0.18);--surf:rgba(13,31,69,0.7);
}
*{box-sizing:border-box;margin:0;padding:0;}
.ord-root{display:flex;min-height:100vh;background:linear-gradient(145deg,var(--navy) 0%,var(--navy3) 60%,#1a3570 100%);font-family:'DM Sans',sans-serif;color:var(--text);}
.ord-root::before{content:'';position:fixed;inset:0;z-index:0;
  background-image:linear-gradient(rgba(28,115,186,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(28,115,186,0.04) 1px,transparent 1px);
  background-size:52px 52px;pointer-events:none;}
/* SIDEBAR */
.ord-sidebar{width:220px;flex-shrink:0;height:100vh;position:sticky;top:0;background:rgba(6,17,43,0.98);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow-y:auto;z-index:20;transition:width 0.3s;}
.ord-sidebar.col{width:56px;}
@media(max-width:820px){.ord-sidebar{position:fixed;left:0;top:0;height:100vh;z-index:100;transform:translateX(0);transition:transform 0.3s;}.ord-sidebar.hidden{transform:translateX(-100%);}}
.ord-sb-head{display:flex;align-items:center;justify-content:space-between;padding:15px 13px;border-bottom:1px solid var(--border);flex-shrink:0;}
.ord-logo{font-family:'Syne',sans-serif;font-size:1.25rem;font-weight:800;color:var(--text);text-decoration:none;}
.ord-logo span{color:var(--gold);}
.ord-col-btn{width:26px;height:26px;border-radius:7px;background:rgba(255,255,255,0.05);border:1px solid var(--border);color:var(--muted);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:12px;transition:all 0.2s;}
.ord-col-btn:hover{background:rgba(28,115,186,0.15);color:var(--blue3);}
.ord-sb-user{padding:12px 13px;border-bottom:1px solid var(--border);flex-shrink:0;display:flex;align-items:center;gap:9px;}
.ord-sb-av{width:32px;height:32px;border-radius:50%;flex-shrink:0;background:linear-gradient(135deg,var(--blue),var(--blue3));display:flex;align-items:center;justify-content:center;font-family:'Syne',sans-serif;font-size:12px;font-weight:800;overflow:hidden;}
.ord-sb-av img{width:100%;height:100%;object-fit:cover;}
.ord-sb-name{font-size:12px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.ord-sb-plan{font-size:9px;color:var(--gold);text-transform:uppercase;letter-spacing:1px;}
.ord-sidebar.col .ord-sb-name,.ord-sidebar.col .ord-sb-plan{display:none;}
.ord-nav-sec{padding:7px 0;}
.ord-nav-sec-title{font-size:9px;font-weight:700;color:rgba(255,255,255,0.18);text-transform:uppercase;letter-spacing:2px;padding:5px 18px 3px;display:block;}
.ord-sidebar.col .ord-nav-sec-title{display:none;}
.ord-nav-item{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:9px;margin:2px 8px;cursor:pointer;border:none;background:transparent;width:calc(100% - 16px);color:var(--muted);font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;text-align:left;transition:all 0.16s;white-space:nowrap;}
.ord-nav-item:hover{background:rgba(28,115,186,0.1);color:var(--text);}
.ord-nav-item.active{background:rgba(28,115,186,0.16);color:var(--blue3);outline:1px solid rgba(28,115,186,0.22);}
.ord-nav-icon{font-size:15px;flex-shrink:0;width:20px;text-align:center;}
.ord-nav-label{flex:1;overflow:hidden;text-overflow:ellipsis;}
.ord-sidebar.col .ord-nav-label{display:none;}
.ord-sidebar.col .ord-nav-item{justify-content:center;padding:10px;width:calc(100% - 8px);margin:2px 4px;}
.ord-sb-bot{margin-top:auto;padding:10px 8px;border-top:1px solid var(--border);flex-shrink:0;}
.ord-logout{display:flex;align-items:center;gap:9px;width:100%;padding:9px 10px;border-radius:9px;background:rgba(248,113,113,0.06);border:1px solid rgba(248,113,113,0.15);color:var(--red);font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.16s;}
.ord-logout:hover{background:rgba(248,113,113,0.12);}
.ord-sidebar.col .ord-logout{justify-content:center;}
.ord-sidebar.col .ord-logout-lbl{display:none;}
/* MAIN */
.ord-main{flex:1;min-width:0;display:flex;flex-direction:column;position:relative;z-index:1;}
.ord-topbar{display:none;align-items:center;justify-content:space-between;padding:0 16px;height:52px;background:rgba(6,17,43,0.98);border-bottom:1px solid var(--border);flex-shrink:0;}
@media(max-width:820px){.ord-topbar{display:flex;}}
.ord-menu-btn{width:32px;height:32px;border-radius:7px;background:rgba(255,255,255,0.05);border:1px solid var(--border);color:var(--text);cursor:pointer;font-size:17px;display:flex;align-items:center;justify-content:center;}
.ord-header{padding:22px 24px 0;flex-shrink:0;}
.ord-title{font-family:'Syne',sans-serif;font-size:1.4rem;font-weight:800;color:var(--text);}
.ord-sub{font-size:11px;color:var(--muted);font-family:'DM Mono',monospace;margin-top:3px;}
.ord-tabs{display:flex;gap:2px;padding:14px 24px 0;border-bottom:1px solid var(--border);flex-shrink:0;}
.ord-tab{padding:9px 16px;border:none;background:transparent;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;color:var(--muted);border-bottom:2px solid transparent;border-radius:6px 6px 0 0;transition:all 0.16s;white-space:nowrap;}
.ord-tab:hover{color:var(--text);background:rgba(255,255,255,0.03);}
.ord-tab.active{color:var(--blue3);border-bottom-color:var(--blue3);background:rgba(28,115,186,0.08);}
.ord-content{flex:1;overflow-y:auto;padding:22px 24px 40px;}
@media(max-width:600px){.ord-content{padding:16px;}}
/* Cards */
.ord-card{background:var(--surf);border:1px solid var(--border);border-radius:16px;padding:20px;backdrop-filter:blur(12px);margin-bottom:12px;}
.ord-card-title{font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:1.8px;margin-bottom:14px;font-family:'DM Mono',monospace;}
/* Input */
.ord-input{width:100%;padding:10px 13px;background:rgba(255,255,255,0.05);border:1px solid rgba(28,115,186,0.2);border-radius:10px;color:var(--text);font-family:'DM Sans',sans-serif;font-size:13px;outline:none;transition:border 0.2s;}
.ord-input:focus{border-color:var(--blue2);}
.ord-select{width:100%;padding:10px 13px;background:rgba(13,31,69,0.8);border:1px solid rgba(28,115,186,0.2);border-radius:10px;color:var(--text);font-family:'DM Sans',sans-serif;font-size:13px;outline:none;cursor:pointer;}
.ord-select option{background:#0d1f45;}
/* Buttons */
.ord-btn-primary{display:inline-flex;align-items:center;gap:7px;padding:10px 20px;border-radius:10px;border:none;background:linear-gradient(135deg,var(--blue),var(--blue2));color:#fff;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:700;cursor:pointer;transition:all 0.18s;box-shadow:0 4px 14px rgba(28,115,186,0.3);}
.ord-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 6px 20px rgba(28,115,186,0.4);}
.ord-btn-primary:disabled{opacity:0.5;cursor:not-allowed;}
.ord-btn-ghost{display:inline-flex;align-items:center;gap:7px;padding:9px 16px;border-radius:10px;border:1px solid var(--border);background:transparent;color:var(--muted);font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;cursor:pointer;transition:all 0.16s;}
.ord-btn-ghost:hover{background:rgba(255,255,255,0.05);color:var(--text);}
/* Status badge */
.ord-badge{font-size:10px;font-weight:700;padding:3px 10px;border-radius:99px;text-transform:uppercase;letter-spacing:1px;display:inline-flex;align-items:center;gap:4px;}
/* Timeline */
.ord-timeline{display:flex;flex-direction:column;gap:0;}
.ord-tl-item{display:flex;gap:14px;padding-bottom:16px;position:relative;}
.ord-tl-item:last-child{padding-bottom:0;}
.ord-tl-dot{width:28px;height:28px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:13px;z-index:1;}
.ord-tl-line{position:absolute;left:14px;top:28px;bottom:0;width:2px;background:rgba(28,115,186,0.15);transform:translateX(-50%);}
.ord-tl-item:last-child .ord-tl-line{display:none;}
/* Alert */
.ord-alert{padding:11px 15px;border-radius:10px;font-size:13px;margin-bottom:14px;display:flex;gap:9px;align-items:flex-start;}
.ord-alert.success{background:rgba(52,211,153,0.1);border:1px solid rgba(52,211,153,0.25);color:var(--green);}
.ord-alert.error{background:rgba(248,113,113,0.1);border:1px solid rgba(248,113,113,0.25);color:var(--red);}
.ord-alert.info{background:rgba(28,115,186,0.1);border:1px solid rgba(28,115,186,0.25);color:var(--blue3);}
/* Grid */
.ord-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
@media(max-width:560px){.ord-grid-2{grid-template-columns:1fr;}}
.ord-col2{grid-column:1/-1;}
/* Overlay */
.ord-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.65);z-index:90;}
@media(max-width:820px){.ord-overlay.show{display:block;}}
::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-track{background:transparent;}::-webkit-scrollbar-thumb{background:rgba(28,115,186,0.25);border-radius:99px;}
`;

const STATUS_COLOR = {
  pending:'#fbbf24', confirmed:'#5aaef7', processing:'#818cf8',
  shipped:'#2dd4bf', delivered:'#34d399', cancelled:'#f87171', refunded:'#94a3b8',
};
const STATUS_ICON = {
  pending:'⏳', confirmed:'✅', processing:'⚙️',
  shipped:'🚚', delivered:'📦', cancelled:'❌', refunded:'↩️',
};
const STATUS_STEPS = ['pending','confirmed','processing','shipped','delivered'];

const PRODUCTS = [
  { id:'card',      name:'Flap Business Card',     price:999,  icon:'💳', desc:'Standard NFC business card' },
  { id:'wallet',    name:'Flap Wallet Card Holder', price:1499, icon:'👜', desc:'NFC wallet with card holder' },
  { id:'keyring',   name:'Flap Keyring',            price:799,  icon:'🔑', desc:'Compact NFC keyring' },
  { id:'ring',      name:'Flap Smart Ring',         price:2499, icon:'💍', desc:'Wearable NFC smart ring' },
  { id:'stand',     name:'Flap Desk Stand',         price:1299, icon:'🪧', desc:'NFC-enabled desk stand with QR' },
  { id:'bundle',    name:'Card + Wallet Bundle',    price:2299, icon:'🎁', desc:'Best value combo pack' },
];

const fldStyle = { display:'flex', flexDirection:'column', gap:5 };
const lblStyle = { fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, fontFamily:"'DM Mono',monospace" };

const NAV = [
  { title:'Orders', items:[
    { icon:'📦', label:'My Orders',   tab:'orders' },
    { icon:'🔍', label:'Track Order', tab:'track'  },
    { icon:'🛒', label:'New Order',   tab:'place'  },
  ]},
  { title:'Account', items:[
    { icon:'📊', label:'Dashboard', path:'/dashboard' },
    { icon:'👤', label:'Profile',   path:'/profile'   },
    { icon:'💬', label:'Messages',  path:'/messages'  },
    { icon:'📈', label:'Analytics', path:'/analytics' },
  ]},
  { title:'More', items:[
    { icon:'🌐', label:'Home',      path:'/'           },
    { icon:'✍️', label:'Write NFC', path:'/write-nfc'  },
  ]},
];

const TABS = [
  {key:'orders', label:'📦 My Orders'},
  {key:'track',  label:'🔍 Track'},
  {key:'place',  label:'🛒 Place Order'},
];

export default function Order() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const [tab,      setTab]      = useState('orders');
  const [user,     setUser]     = useState(null);
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [col,      setCol]      = useState(false);
  const [sideOpen, setSideOpen] = useState(false);

  // Track order
  const [trackId,  setTrackId]  = useState('');
  const [tracked,  setTracked]  = useState(null);
  const [tracking, setTracking] = useState(false);
  const [trackErr, setTrackErr] = useState('');

  // Place order
  const [form, setForm] = useState({
    product:'card', quantity:1, variant:'',
    name:'', email:'', phone:'', address:'', city:'', notes:'',
  });
  const [placing,  setPlacing]  = useState(false);
  const [placeAlert,setPlaceAlert]=useState(null);

  useEffect(() => {
    api.get('/users/profile').then(r => {
      setUser(r.data);
      setForm(p => ({
        ...p,
        name:  r.data.username || '',
        email: r.data.email    || '',
        phone: r.data.phone_number_1 || '',
      }));
    }).catch(() => navigate('/login'));

    api.get('/orders/my').catch(() => api.get('/admin/orders/my'))
      .then(r => setOrders(Array.isArray(r.data) ? r.data : r.data.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => { localStorage.removeItem('token'); navigate('/login'); };

  // Track
  const doTrack = async () => {
    if (!trackId.trim()) return;
    setTracking(true); setTrackErr(''); setTracked(null);
    try {
      const r = await api.get(`/orders/${trackId.trim()}`).catch(() =>
        api.get(`/admin/orders/${trackId.trim()}`)
      );
      setTracked(r.data);
    } catch {
      // search local orders
      const local = orders.find(o =>
        o._id === trackId || o.orderNumber === trackId ||
        o._id?.slice(-8).toUpperCase() === trackId.toUpperCase()
      );
      if (local) setTracked(local);
      else setTrackErr('Order not found. Check the order ID and try again.');
    }
    setTracking(false);
  };

  // Place order
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const placeOrder = async () => {
    if (!form.name || !form.phone || !form.address)
      return setPlaceAlert({type:'error',msg:'Name, phone and address are required.'});
    const prod = PRODUCTS.find(p=>p.id===form.product);
    setPlacing(true);
    try {
      await api.post('/orders', {
        items: [{ productId: form.product, productName: prod?.name, quantity: Number(form.quantity), unitPrice: prod?.price*100, variantName: form.variant||undefined }],
        shippingAddress: { name:form.name, phone:form.phone, address:form.address, city:form.city },
        notes: form.notes || undefined,
        totalAmount: (prod?.price||0) * Number(form.quantity),
      });
      setPlaceAlert({type:'success',msg:'🎉 Order placed! We will contact you to confirm.'});
      setForm(p=>({...p,quantity:1,variant:'',notes:''}));
      // Reload orders
      api.get('/orders/my').catch(()=>api.get('/admin/orders/my'))
        .then(r=>setOrders(Array.isArray(r.data)?r.data:r.data.orders||[]))
        .catch(()=>{});
    } catch(e) {
      setPlaceAlert({type:'error',msg:e.response?.data?.error||'Could not place order.'});
    }
    setPlacing(false);
  };

  // Helpers
  const stepIdx = (status) => STATUS_STEPS.indexOf(status);

  const Sidebar = () => (
    <nav className={`ord-sidebar ${col?'col':''} ${!sideOpen&&window.innerWidth<=820?'hidden':''}`}>
      <div className="ord-sb-head">
        {!col&&<a href="/" className="ord-logo">flap<span>.</span></a>}
        <button className="ord-col-btn" onClick={()=>{setCol(c=>!c);setSideOpen(false);}}>
          {col?'→':'←'}
        </button>
      </div>
      <div className="ord-sb-user">
        <div className="ord-sb-av">
          {user?.user_photo?<img src={user.user_photo} alt=""/>:(user?.username||'?')[0].toUpperCase()}
        </div>
        {!col&&<div style={{minWidth:0}}>
          <div className="ord-sb-name">{user?.username||user?.email?.split('@')[0]||'User'}</div>
          <div className="ord-sb-plan">{user?.plan||'free'}</div>
        </div>}
      </div>
      {NAV.map(sec=>(
        <div className="ord-nav-sec" key={sec.title}>
          <span className="ord-nav-sec-title">{sec.title}</span>
          {sec.items.map(item=>{
            const isActive = item.tab ? tab===item.tab : location.pathname===item.path;
            return (
              <button key={item.tab||item.path} className={`ord-nav-item ${isActive?'active':''}`}
                onClick={()=>{ if(item.tab){setTab(item.tab);setSideOpen(false);} else navigate(item.path); }}>
                <span className="ord-nav-icon">{item.icon}</span>
                <span className="ord-nav-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      ))}
      <div className="ord-sb-bot">
        <button className="ord-logout" onClick={logout}>
          <span style={{fontSize:14}}>⏻</span>
          <span className="ord-logout-lbl">Log Out</span>
        </button>
      </div>
    </nav>
  );

  const StatusBadge = ({status}) => (
    <span className="ord-badge" style={{background:`${STATUS_COLOR[status]||'#94a3b8'}18`,color:STATUS_COLOR[status]||'#94a3b8',border:`1px solid ${STATUS_COLOR[status]||'#94a3b8'}40`}}>
      {STATUS_ICON[status]} {status}
    </span>
  );

  const OrderCard = ({o}) => {
    const open = selected?._id===o._id;
    return (
      <div className="ord-card" style={{cursor:'pointer',transition:'border 0.2s',borderColor:open?'rgba(28,115,186,0.4)':'rgba(28,115,186,0.18)'}} onClick={()=>setSelected(open?null:o)}>
        {/* Header row */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:'var(--blue3)',fontFamily:"'DM Mono',monospace",marginBottom:3}}>
              #{o.orderNumber || o._id?.slice(-8).toUpperCase()}
            </div>
            <div style={{fontSize:11,color:'var(--muted)'}}>
              {new Date(o.createdAt).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'})} · {o.items?.length||0} item{(o.items?.length||0)!==1?'s':''}
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
            <div style={{fontSize:18,fontWeight:800,color:'var(--blue3)',fontFamily:"'Syne',sans-serif"}}>
              Rs. {(o.totalAmount||0).toLocaleString()}
            </div>
            <StatusBadge status={o.status||'pending'}/>
            <span style={{color:'var(--muted)',fontSize:16}}>{open?'▲':'▼'}</span>
          </div>
        </div>

        {/* Expanded detail */}
        {open && (
          <div style={{marginTop:18,paddingTop:18,borderTop:'1px solid var(--border)'}}>

            {/* Progress bar */}
            {!['cancelled','refunded'].includes(o.status) && (
              <div style={{marginBottom:20}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                  {STATUS_STEPS.map((s,i)=>{
                    const done = stepIdx(o.status)>=i;
                    return (
                      <div key={s} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,flex:1}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:done?`rgba(28,115,186,0.2)`:'rgba(255,255,255,0.04)',border:`2px solid ${done?'var(--blue3)':'rgba(255,255,255,0.1)'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12}}>
                          {done?'✓':i+1}
                        </div>
                        <div style={{fontSize:8,color:done?'var(--blue3)':'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,textAlign:'center'}}>{s}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{height:3,background:'rgba(255,255,255,0.05)',borderRadius:99,overflow:'hidden'}}>
                  <div style={{height:'100%',borderRadius:99,background:'linear-gradient(90deg,var(--blue),var(--blue3))',width:`${Math.max(10,(stepIdx(o.status)+1)/STATUS_STEPS.length*100)}%`,transition:'width 0.8s ease'}}/>
                </div>
              </div>
            )}

            {/* Items */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1.5,marginBottom:10,fontFamily:"'DM Mono',monospace"}}>Order Items</div>
              {(o.items||[]).map((item,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 12px',background:'rgba(255,255,255,0.03)',borderRadius:10,marginBottom:6}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{item.productName||item.productType||'Item'}</div>
                    {item.variantName&&<div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>Variant: {item.variantName}</div>}
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:12,color:'var(--muted)'}}>×{item.quantity}</div>
                    <div style={{fontSize:13,fontWeight:700,color:'var(--blue3)',fontFamily:"'DM Mono',monospace"}}>Rs. {((item.totalPrice||item.unitPrice*item.quantity||0)/100).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Shipping + notes */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              {o.shippingAddress&&(
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1.5,marginBottom:7,fontFamily:"'DM Mono',monospace"}}>Delivery Address</div>
                  <div style={{fontSize:12,color:'var(--text)',lineHeight:1.7}}>
                    {o.shippingAddress.name&&<div style={{fontWeight:600}}>{o.shippingAddress.name}</div>}
                    {o.shippingAddress.phone&&<div>{o.shippingAddress.phone}</div>}
                    {o.shippingAddress.address&&<div style={{color:'var(--muted)'}}>{o.shippingAddress.address}</div>}
                    {o.shippingAddress.city&&<div style={{color:'var(--muted)'}}>{o.shippingAddress.city}</div>}
                  </div>
                </div>
              )}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1.5,marginBottom:7,fontFamily:"'DM Mono',monospace"}}>Summary</div>
                <div style={{fontSize:12,lineHeight:1.9}}>
                  <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'var(--muted)'}}>Order ID</span><span style={{fontFamily:"'DM Mono',monospace",color:'var(--blue3)',fontSize:11}}>{o.orderNumber||o._id?.slice(-8)}</span></div>
                  <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'var(--muted)'}}>Payment</span><span style={{color:'var(--text)'}}>{o.paymentMethod||'COD'}</span></div>
                  <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'var(--muted)'}}>Total</span><span style={{fontWeight:700,color:'var(--green)'}}>Rs. {(o.totalAmount||0).toLocaleString()}</span></div>
                </div>
              </div>
            </div>

            {o.notes&&(
              <div style={{marginTop:12,padding:'10px 13px',background:'rgba(255,255,255,0.03)',borderRadius:10,fontSize:12,color:'var(--muted)',fontStyle:'italic'}}>
                📝 {o.notes}
              </div>
            )}

            {/* NFC Units if assigned */}
            {(o.items||[]).some(i=>i.units?.length>0)&&(
              <div style={{marginTop:14}}>
                <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,fontFamily:"'DM Mono',monospace"}}>NFC Units</div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {(o.items||[]).flatMap(i=>i.units||[]).map((u,i)=>(
                    <div key={i} style={{padding:'7px 13px',borderRadius:9,background:'rgba(28,115,186,0.1)',border:'1px solid rgba(28,115,186,0.2)'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--blue3)',fontFamily:"'DM Mono',monospace"}}>{u.flapid||'—'}</div>
                      {u.uid&&<div style={{fontSize:9,color:'var(--muted)'}}>UID: {u.uid}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const selectedProd = PRODUCTS.find(p=>p.id===form.product);

  return (
    <div className="ord-root">
      <style>{CSS}</style>
      <Sidebar/>
      <div className={`ord-overlay ${sideOpen?'show':''}`} onClick={()=>setSideOpen(false)}/>

      <div className="ord-main">
        {/* Mobile topbar */}
        <div className="ord-topbar">
          <button className="ord-menu-btn" onClick={()=>setSideOpen(s=>!s)}>☰</button>
          <a href="/" className="ord-logo" style={{textDecoration:'none'}}>flap<span style={{color:'var(--gold)'}}>.</span></a>
          <button className="ord-btn-ghost" style={{padding:'5px 12px',fontSize:11}} onClick={()=>setTab('place')}>🛒 Order</button>
        </div>

        {/* Header */}
        <div className="ord-header">
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:10,marginBottom:6}}>
            <div>
              <div className="ord-title">
                {tab==='orders'?'📦 My Orders':tab==='track'?'🔍 Track Order':'🛒 Place Order'}
              </div>
              <div className="ord-sub">
                {tab==='orders'?`${orders.length} order${orders.length!==1?'s':''}`:
                 tab==='track'?'Enter your order ID or number':
                 'Choose a product and fill in delivery details'}
              </div>
            </div>
            {tab==='orders'&&(
              <button className="ord-btn-primary" onClick={()=>setTab('place')}>🛒 New Order</button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="ord-tabs">
          {TABS.map(t=>(
            <button key={t.key} className={`ord-tab ${tab===t.key?'active':''}`} onClick={()=>setTab(t.key)}>{t.label}</button>
          ))}
        </div>

        <div className="ord-content">

          {/* ══ MY ORDERS TAB ══ */}
          {tab==='orders'&&(
            <>
              {loading&&<div style={{textAlign:'center',color:'var(--muted)',padding:'60px 0',fontFamily:"'DM Mono',monospace",fontSize:13}}>Loading orders…</div>}
              {!loading&&orders.length===0&&(
                <div style={{textAlign:'center',padding:'80px 20px'}}>
                  <div style={{fontSize:52,marginBottom:14}}>📦</div>
                  <div style={{fontSize:18,fontWeight:800,color:'var(--text)',fontFamily:"'Syne',sans-serif",marginBottom:8}}>No orders yet</div>
                  <div style={{fontSize:13,color:'var(--muted)',marginBottom:22}}>Your order history will appear here once you place an order.</div>
                  <button className="ord-btn-primary" onClick={()=>setTab('place')}>🛒 Place Your First Order</button>
                </div>
              )}
              {orders.map(o=><OrderCard key={o._id} o={o}/>)}
            </>
          )}

          {/* ══ TRACK TAB ══ */}
          {tab==='track'&&(
            <div style={{maxWidth:560}}>
              <div className="ord-card">
                <div className="ord-card-title">Enter Order Details</div>
                <div style={{display:'flex',gap:10,marginBottom:16}}>
                  <input className="ord-input" value={trackId} onChange={e=>setTrackId(e.target.value)}
                    placeholder="Order ID, number, or last 8 chars…"
                    onKeyDown={e=>e.key==='Enter'&&doTrack()}
                    style={{flex:1}}/>
                  <button className="ord-btn-primary" onClick={doTrack} disabled={tracking||!trackId.trim()}>
                    {tracking?'⏳':'🔍'} Track
                  </button>
                </div>
                {trackErr&&<div className="ord-alert error">{trackErr}</div>}
                {/* Quick select from own orders */}
                {orders.length>0&&(
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1.5,marginBottom:8,fontFamily:"'DM Mono',monospace"}}>Quick Track — Recent Orders</div>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {orders.slice(0,5).map(o=>(
                        <button key={o._id} onClick={()=>{setTracked(o);setTrackId(o.orderNumber||o._id?.slice(-8));}}
                          style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',background:'rgba(255,255,255,0.03)',border:'1px solid var(--border)',borderRadius:9,cursor:'pointer',textAlign:'left',transition:'background 0.15s'}}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(28,115,186,0.08)'}
                          onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.03)'}>
                          <div>
                            <div style={{fontSize:12,fontWeight:700,color:'var(--blue3)',fontFamily:"'DM Mono',monospace"}}>{o.orderNumber||'#'+o._id?.slice(-8).toUpperCase()}</div>
                            <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>{new Date(o.createdAt).toLocaleDateString()}</div>
                          </div>
                          <StatusBadge status={o.status||'pending'}/>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {tracked&&(
                <div className="ord-card" style={{borderColor:'rgba(28,115,186,0.35)'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                    <div style={{fontSize:14,fontWeight:700,color:'var(--text)',fontFamily:"'Syne',sans-serif"}}>
                      {STATUS_ICON[tracked.status]} Order {tracked.orderNumber||('#'+tracked._id?.slice(-8).toUpperCase())}
                    </div>
                    <StatusBadge status={tracked.status||'pending'}/>
                  </div>

                  {/* Timeline */}
                  <div className="ord-timeline">
                    {STATUS_STEPS.map((s,i)=>{
                      const done = stepIdx(tracked.status)>=i;
                      const current = tracked.status===s;
                      return (
                        <div key={s} className="ord-tl-item">
                          <div className="ord-tl-dot" style={{background:done?'rgba(28,115,186,0.2)':'rgba(255,255,255,0.04)',border:`2px solid ${done?'var(--blue3)':'rgba(255,255,255,0.1)'}`}}>
                            {done?'✓':i+1}
                          </div>
                          <div className="ord-tl-line"/>
                          <div style={{flex:1,paddingTop:2}}>
                            <div style={{fontSize:13,fontWeight:current?700:500,color:current?'var(--blue3)':done?'var(--text)':'var(--muted)',textTransform:'capitalize'}}>{s}</div>
                            {current&&<div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>Current status</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{marginTop:16,padding:'12px 14px',background:'rgba(255,255,255,0.03)',borderRadius:10,fontSize:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                      <span style={{color:'var(--muted)'}}>Placed</span>
                      <span>{new Date(tracked.createdAt).toLocaleString()}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between'}}>
                      <span style={{color:'var(--muted)'}}>Total</span>
                      <span style={{fontWeight:700,color:'var(--green)'}}>Rs. {(tracked.totalAmount||0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ PLACE ORDER TAB ══ */}
          {tab==='place'&&(
            <div style={{maxWidth:680}}>
              {placeAlert&&(
                <div className={`ord-alert ${placeAlert.type}`}>
                  <span style={{flex:1}}>{placeAlert.msg}</span>
                  <button onClick={()=>setPlaceAlert(null)} style={{background:'transparent',border:'none',color:'inherit',cursor:'pointer',fontSize:14,padding:0}}>✕</button>
                </div>
              )}

              {/* Product picker */}
              <div className="ord-card">
                <div className="ord-card-title">Choose Product</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:10,marginBottom:16}}>
                  {PRODUCTS.map(p=>(
                    <button key={p.id} onClick={()=>set('product',p.id)}
                      style={{padding:'14px 12px',borderRadius:12,border:`1px solid ${form.product===p.id?'rgba(28,115,186,0.5)':'rgba(255,255,255,0.07)'}`,background:form.product===p.id?'rgba(28,115,186,0.12)':'rgba(255,255,255,0.03)',cursor:'pointer',textAlign:'center',transition:'all 0.16s'}}>
                      <div style={{fontSize:24,marginBottom:5}}>{p.icon}</div>
                      <div style={{fontSize:12,fontWeight:700,color:form.product===p.id?'var(--blue3)':'var(--text)',lineHeight:1.3,marginBottom:4}}>{p.name}</div>
                      <div style={{fontSize:11,color:'var(--muted)',marginBottom:6}}>{p.desc}</div>
                      <div style={{fontSize:14,fontWeight:800,color:'var(--green)',fontFamily:"'DM Mono',monospace"}}>Rs. {p.price.toLocaleString()}</div>
                    </button>
                  ))}
                </div>

                <div className="ord-grid-2">
                  <div style={fldStyle}>
                    <label style={lblStyle}>Quantity</label>
                    <select className="ord-select" value={form.quantity} onChange={e=>set('quantity',e.target.value)}>
                      {[1,2,3,5,10,20,50].map(n=><option key={n} value={n}>{n} unit{n>1?'s':''}</option>)}
                    </select>
                  </div>
                  <div style={fldStyle}>
                    <label style={lblStyle}>Finish / Variant</label>
                    <select className="ord-select" value={form.variant} onChange={e=>set('variant',e.target.value)}>
                      <option value="">Standard</option>
                      <option value="matte">Matte Black</option>
                      <option value="glossy">Glossy White</option>
                      <option value="gold">Gold Edition</option>
                      <option value="custom">Custom (specify in notes)</option>
                    </select>
                  </div>
                </div>

                {selectedProd&&(
                  <div style={{marginTop:14,padding:'12px 14px',background:'rgba(28,115,186,0.08)',borderRadius:10,border:'1px solid rgba(28,115,186,0.18)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:13,color:'var(--text)'}}>{selectedProd.icon} {selectedProd.name} × {form.quantity}</span>
                    <span style={{fontSize:16,fontWeight:800,color:'var(--green)',fontFamily:"'DM Mono',monospace"}}>Rs. {(selectedProd.price*form.quantity).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Delivery */}
              <div className="ord-card">
                <div className="ord-card-title">Delivery Details</div>
                <div className="ord-grid-2">
                  <div style={fldStyle}>
                    <label style={lblStyle}>Full Name *</label>
                    <input className="ord-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Your full name"/>
                  </div>
                  <div style={fldStyle}>
                    <label style={lblStyle}>Phone *</label>
                    <input className="ord-input" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+977 98XXXXXXXX"/>
                  </div>
                  <div style={fldStyle}>
                    <label style={lblStyle}>Email</label>
                    <input className="ord-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="your@email.com"/>
                  </div>
                  <div style={fldStyle}>
                    <label style={lblStyle}>City</label>
                    <input className="ord-input" value={form.city} onChange={e=>set('city',e.target.value)} placeholder="Kathmandu"/>
                  </div>
                  <div style={{...fldStyle, gridColumn:'1/-1'}}>
                    <label style={lblStyle}>Delivery Address *</label>
                    <textarea className="ord-input" style={{resize:'vertical',minHeight:70}} value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Street, area, landmark…"/>
                  </div>
                  <div style={{...fldStyle, gridColumn:'1/-1'}}>
                    <label style={lblStyle}>Notes / Special Instructions</label>
                    <textarea className="ord-input" style={{resize:'vertical',minHeight:54}} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Custom design, color preference, message…"/>
                  </div>
                </div>
              </div>

              {/* Payment info */}
              <div className="ord-card" style={{borderColor:'rgba(245,166,35,0.2)'}}>
                <div className="ord-card-title" style={{color:'var(--gold)'}}>💳 Payment</div>
                <div style={{fontSize:13,color:'var(--muted)',lineHeight:1.7,marginBottom:14}}>
                  We currently accept <strong style={{color:'var(--text)'}}>Cash on Delivery (COD)</strong> and <strong style={{color:'var(--text)'}}>eSewa / Khalti</strong>.<br/>
                  Our team will contact you on WhatsApp to confirm and arrange payment after placing your order.
                </div>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                  <button className="ord-btn-primary" onClick={placeOrder} disabled={placing||!form.name||!form.phone||!form.address}>
                    {placing?'⏳ Placing Order…':'🛒 Place Order'}
                  </button>
                  <div style={{fontSize:12,color:'var(--muted)'}}>
                    Total: <strong style={{color:'var(--green)'}}>Rs. {((selectedProd?.price||0)*form.quantity).toLocaleString()}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}